import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, UserPlus, Mail, Trash2 } from "lucide-react";
import { API, authHeaders, errorText } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { useAsk } from "@/components/admin/AskDialog";

export const AREAS = [["leads", "Leads & people", "See and update every lead, set CRM stages, archive test leads."],
  ["learning", "Learning portal", "Batches and Zoom links, students, sessions, study material, project reviews."],
  ["content", "Blog & toolkit book", "Write and publish blog posts, replace toolkit files."]];
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : null);
const inputCls = "h-11 w-full rounded-lg border border-slate-300 px-3 outline-none focus:border-[#0E9384]";

const AreaPicker = ({ value, onChange, idPrefix }) => (
  <div className="grid gap-2 md:grid-cols-3">{AREAS.map(([k, l, d]) => (
    <label key={k} htmlFor={`${idPrefix}-${k}`} className={`flex cursor-pointer gap-2.5 rounded-lg border p-3 ${value.includes(k) ? "border-[#0E9384] bg-teal-50" : "border-slate-200 bg-white"}`}>
      <Checkbox id={`${idPrefix}-${k}`} checked={value.includes(k)} onCheckedChange={(v) => onChange(v ? [...value, k] : value.filter((x) => x !== k))}
        className="mt-0.5 data-[state=checked]:border-[#0E9384] data-[state=checked]:bg-[#0E9384]" />
      <span><b className="block text-[14px] text-[#0B1B33]">{l}</b><span className="text-[12.5px] text-slate-600">{d}</span></span>
    </label>))}</div>
);

export const Team = () => {
  const ask = useAsk();
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ name: "", email: "", permissions: ["leads", "learning"] });
  const [busy, setBusy] = useState(false);
  const h = { headers: authHeaders() };

  const load = useCallback(() => axios.get(`${API}/admin/team`, { headers: authHeaders() })
    .then((r) => setRows(r.data)).catch((e) => { setRows([]); toast.error(errorText(e)); }), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!f.permissions.length) return toast.error("Pick at least one area.");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/team`, f, h);
      toast.success(`Invite sent to ${f.email}`);
      setF({ name: "", email: "", permissions: ["leads", "learning"] });
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); load(); }
  };
  const patch = async (u, body, msg) => {
    try { await axios.patch(`${API}/admin/team/${u.id}`, body, h); toast.success(msg); load(); } catch (err) { toast.error(errorText(err)); }
  };
  const reinvite = async (u) => {
    try { await axios.post(`${API}/admin/team/${u.id}/invite`, {}, h); toast.success(`New link sent to ${u.email}`); load(); } catch (err) { toast.error(errorText(err)); }
  };
  const remove = async (u) => {
    if (!(await ask({ title: `Remove ${u.name}?`, description:"They lose admin access straight away, including any session they have open.", confirmLabel: "Remove", danger: true }))) return;
    try { await axios.delete(`${API}/admin/team/${u.id}`, h); toast.success("Removed"); load(); } catch (err) { toast.error(errorText(err)); }
  };

  return (
    <div data-testid="admin-team" className="max-w-4xl space-y-6">
      <form onSubmit={add} className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
        <div><h2 className="font-display text-[18px] font-bold text-[#0B1B33]">Add a coordinator</h2>
          <p className="text-[14px] text-slate-600">They get an email to set their own password. They only see the areas you tick. Settings, this team page and permanent delete stay with you.</p></div>
        <div className="grid gap-3 md:grid-cols-2">
          <input id="team-name" data-testid="team-name" required maxLength={80} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
          <input id="team-email" data-testid="team-email" required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
        </div>
        <AreaPicker idPrefix="new" value={f.permissions} onChange={(permissions) => setF({ ...f, permissions })} />
        <button data-testid="team-add" disabled={busy} className="btn-teal flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Send invite</button>
      </form>

      {!rows ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#0E9384]" /> : !rows.length ? (
        <p className="text-[14px] text-slate-600">No coordinators yet. Only you can sign in.</p>
      ) : rows.map((u) => (
        <section key={u.id} data-testid="team-member" className={`space-y-3 rounded-xl border bg-white p-5 ${u.disabled ? "border-slate-200 opacity-70" : "border-slate-200"}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-display text-[17px] font-bold text-[#0B1B33]">{u.name} {u.disabled && <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[12px] font-semibold text-slate-600">Paused</span>}
                {!u.joined_at && !u.disabled && <span className="ml-1 rounded-full bg-amber-50 px-2 py-0.5 text-[12px] font-semibold text-amber-800">Invite pending</span>}</p>
              <p className="text-[14px] text-slate-600">{u.email}</p>
              <p className="mt-1 text-[12.5px] text-slate-500">{u.last_login ? `Last signed in ${when(u.last_login)}` : u.joined_at ? `Joined ${when(u.joined_at)}` : `Invited ${when(u.invited_at)}`}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => reinvite(u)} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"><Mail className="h-4 w-4" /> {u.joined_at ? "Send password reset" : "Resend invite"}</button>
              <button onClick={() => patch(u, { disabled: !u.disabled }, u.disabled ? "Access restored" : "Access paused")} className="rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50">{u.disabled ? "Restore access" : "Pause access"}</button>
              <button onClick={() => remove(u)} className="flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-[13px] font-semibold text-red-700 hover:bg-red-100"><Trash2 className="h-4 w-4" /> Remove</button>
            </div>
          </div>
          <AreaPicker idPrefix={u.id} value={u.permissions || []} onChange={(permissions) => permissions.length ? patch(u, { permissions }, "Access updated") : toast.error("Keep at least one area, or pause their access.")} />
        </section>
      ))}
    </div>
  );
};

export const AcceptInvite = ({ token, onDone }) => {
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a.length < 10) return toast.error("Use at least 10 characters.");
    if (pw.a !== pw.b) return toast.error("The two passwords don't match.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/invite/accept`, { token, password: pw.a });
      localStorage.setItem("aor_admin_token", data.token);
      window.history.replaceState(null, "", "/admin#home");
      toast.success(`Welcome, ${data.user.name.split(" ")[0]}`);
      onDone();
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-screen place-items-center bg-[#0B1B33] px-5">
      <form data-testid="invite-card" onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-8">
        <h1 className="font-display text-[24px] font-bold text-[#0B1B33]">Set your admin password</h1>
        <p className="text-[14px] text-slate-600">At least 10 characters. Four random words are easy to remember and hard to guess.</p>
        <input id="invite-pw" data-testid="invite-pw" type="password" autoComplete="new-password" required placeholder="New password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className={inputCls} />
        <input id="invite-pw2" data-testid="invite-pw2" type="password" autoComplete="new-password" required placeholder="Repeat it" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className={inputCls} />
        <button data-testid="invite-submit" disabled={busy} className="btn-teal w-full rounded-lg py-3 font-semibold disabled:opacity-60">{busy ? "Saving..." : "Save and sign in"}</button>
      </form>
    </div>
  );
};
